import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiResponse } from '../models/api-response';
import { LessonAttachmentDto } from '../models/course';
import {
  ContentFolderDto,
  ContentItemDetailDto,
  ContentItemDto,
  ContentLibraryPageDto,
  ContentLibrarySearchDto,
  ContentUploadMeta,
  CourseAnalyticsDto,
  CourseItemAnalysisDto,
  CreateContentLinkDto,
  LibraryView,
  TenantLmsOverviewDto,
  UpdateContentItemDto,
  UpsertContentFolderDto,
} from '../models/lms-library';

/** LMS L6 API client: content library, storage usage and course analytics. Auth + X-Tenant-ID come from the JwtInterceptor. */
@Injectable({ providedIn: 'root' })
export class LmsLibraryService {
  private readonly api = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ── Content library ─────────────────────────────────────────────────────
  browse(search: ContentLibrarySearchDto): Observable<ApiResponse<ContentLibraryPageDto>> {
    return this.http.post<ApiResponse<ContentLibraryPageDto>>(`${this.api}/ContentLibrary/Browse`, search);
  }
  folders(view: LibraryView): Observable<ApiResponse<ContentFolderDto[]>> {
    return this.http.get<ApiResponse<ContentFolderDto[]>>(`${this.api}/ContentLibrary/Folders`, { params: { view } });
  }
  item(id: string): Observable<ApiResponse<ContentItemDetailDto>> {
    return this.http.get<ApiResponse<ContentItemDetailDto>>(`${this.api}/ContentLibrary/Item/${id}`);
  }
  createFolder(dto: UpsertContentFolderDto): Observable<ApiResponse<ContentFolderDto>> {
    return this.http.post<ApiResponse<ContentFolderDto>>(`${this.api}/ContentLibrary/CreateFolder`, dto);
  }
  updateFolder(dto: UpsertContentFolderDto): Observable<ApiResponse<ContentFolderDto>> {
    return this.http.put<ApiResponse<ContentFolderDto>>(`${this.api}/ContentLibrary/UpdateFolder`, dto);
  }
  deleteFolder(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/ContentLibrary/DeleteFolder/${id}`);
  }
  upload(file: File, meta: ContentUploadMeta): Observable<HttpEvent<ApiResponse<ContentItemDto>>> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('scope', String(meta.scope));
    if (meta.title) form.append('title', meta.title);
    if (meta.description) form.append('description', meta.description);
    if (meta.folderId) form.append('folderId', meta.folderId);
    if (meta.tags?.length) form.append('tags', meta.tags.join(','));
    return this.http.post<ApiResponse<ContentItemDto>>(`${this.api}/ContentLibrary/Upload`, form, { reportProgress: true, observe: 'events' });
  }
  createLink(dto: CreateContentLinkDto): Observable<ApiResponse<ContentItemDto>> {
    return this.http.post<ApiResponse<ContentItemDto>>(`${this.api}/ContentLibrary/CreateLink`, dto);
  }
  updateItem(dto: UpdateContentItemDto): Observable<ApiResponse<ContentItemDto>> {
    return this.http.put<ApiResponse<ContentItemDto>>(`${this.api}/ContentLibrary/UpdateItem`, dto);
  }
  deleteItem(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/ContentLibrary/DeleteItem/${id}`);
  }
  /** Attach a library file to a lesson by reference. */
  attachFromLibrary(lessonId: string, contentItemId: string): Observable<ApiResponse<LessonAttachmentDto>> {
    return this.http.post<ApiResponse<LessonAttachmentDto>>(`${this.api}/CourseContent/AddAttachment`, {
      lessonId,
      contentItemId,
    });
  }

  // ── Analytics ───────────────────────────────────────────────────────────
  courseAnalytics(courseId: string, days: number): Observable<ApiResponse<CourseAnalyticsDto>> {
    return this.http.get<ApiResponse<CourseAnalyticsDto>>(`${this.api}/LmsAnalytics/Course/${courseId}`, { params: { days } });
  }
  itemAnalysis(courseId: string): Observable<ApiResponse<CourseItemAnalysisDto>> {
    return this.http.get<ApiResponse<CourseItemAnalysisDto>>(`${this.api}/LmsAnalytics/ItemAnalysis/${courseId}`);
  }
  exportAnalytics(courseId: string, days: number, format: 'xlsx' | 'csv'): Observable<Blob> {
    const params = new HttpParams().set('days', days).set('format', format);
    return this.http.get(`${this.api}/LmsAnalytics/Export/${courseId}`, { params, responseType: 'blob' });
  }
  tenantOverview(): Observable<ApiResponse<TenantLmsOverviewDto>> {
    return this.http.get<ApiResponse<TenantLmsOverviewDto>>(`${this.api}/LmsAnalytics/TenantOverview`);
  }
}
